import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Download, X, Smartphone, Monitor, Tablet, Check, Zap } from 'lucide-react';

export const InstallPWA = ({ variant = 'banner' }) => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [deviceType, setDeviceType] = useState('desktop');

  useEffect(() => {
    // Detectar tipo de dispositivo
    const userAgent = navigator.userAgent.toLowerCase();
    if (/tablet|ipad|playbook|silk/i.test(userAgent)) {
      setDeviceType('tablet');
    } else if (/mobile|iphone|ipod|android|blackberry|opera mini|iemobile/i.test(userAgent)) {
      setDeviceType('mobile');
    } else {
      setDeviceType('desktop');
    }

    // Verificar se já está instalado
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Verificar se já foi instalado via navigator
    if (navigator.standalone === true) {
      setIsInstalled(true);
      return;
    }

    // Escutar evento de instalação
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Mostrar banner se não foi dispensado recentemente
      const dismissed = localStorage.getItem('pwa-banner-dismissed');
      const dismissedTime = dismissed ? parseInt(dismissed) : 0;
      const dayInMs = 24 * 60 * 60 * 1000;
      
      if (!dismissed || Date.now() - dismissedTime > dayInMs * 7) {
        setShowInstallBanner(true);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // Detectar quando foi instalado
    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setShowInstallBanner(false);
      setDeferredPrompt(null);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) {
      // Se não tem prompt, mostrar instruções manuais
      setShowModal(true);
      return;
    }

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setIsInstalled(true);
    }
    
    setDeferredPrompt(null);
    setShowInstallBanner(false);
  };

  const dismissBanner = () => {
    setShowInstallBanner(false);
    localStorage.setItem('pwa-banner-dismissed', Date.now().toString());
  };

  const getDeviceIcon = () => {
    switch (deviceType) {
      case 'tablet': return <Tablet className="w-6 h-6" />;
      case 'mobile': return <Smartphone className="w-6 h-6" />;
      default: return <Monitor className="w-6 h-6" />;
    }
  };

  const getInstructions = () => {
    const isIOS = /iphone|ipad|ipod/.test(navigator.userAgent.toLowerCase());
    const isChrome = /chrome/.test(navigator.userAgent.toLowerCase());
    const isFirefox = /firefox/.test(navigator.userAgent.toLowerCase());
    const isEdge = /edg/.test(navigator.userAgent.toLowerCase());
    const isSafari = /safari/.test(navigator.userAgent.toLowerCase()) && !isChrome;

    if (isIOS) {
      return {
        title: 'Instalar no iPhone/iPad',
        steps: [
          'Toque no botão de compartilhar (ícone de quadrado com seta)',
          'Role para baixo e toque em "Adicionar à Tela de Início"',
          'Toque em "Adicionar" para confirmar'
        ]
      };
    }

    if (isChrome || isEdge) {
      return {
        title: 'Instalar no Chrome/Edge',
        steps: [
          'Clique no ícone de instalação (⊕) na barra de endereços',
          'Ou clique nos 3 pontos (⋮) → "Instalar nfcesysPdv"',
          'Clique em "Instalar" para confirmar'
        ]
      };
    }

    if (isFirefox) {
      return {
        title: 'Instalar no Firefox',
        steps: [
          'Clique no ícone de casa (🏠) na barra de endereços',
          'Selecione "Adicionar à Tela Inicial"',
          'Confirme a instalação'
        ]
      };
    }

    if (isSafari) {
      return {
        title: 'Instalar no Safari',
        steps: [
          'Clique em "Arquivo" no menu',
          'Selecione "Adicionar à Dock"',
          'Ou use o botão de compartilhar'
        ]
      };
    }

    return {
      title: 'Instalar o Aplicativo',
      steps: [
        'Procure o ícone de instalação na barra de endereços',
        'Clique em "Instalar" ou "Adicionar à tela inicial"',
        'Confirme a instalação'
      ]
    };
  };

  // Se já instalado, não mostrar nada
  if (isInstalled) return null;

  // Variante: Apenas botão
  if (variant === 'button') {
    return (
      <>
        <Button
          onClick={handleInstall}
          className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white"
        >
          <Download className="w-4 h-4 mr-2" />
          Instalar App
        </Button>
        
        {showModal && <InstallModal onClose={() => setShowModal(false)} instructions={getInstructions()} />}
      </>
    );
  }

  // Variante: Card completo
  if (variant === 'card') {
    return (
      <>
        <Card className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 border-amber-500/30">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl flex items-center justify-center text-white shadow-lg">
                {getDeviceIcon()}
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-slate-900 mb-1">
                  Instalar nfcesysPdv
                </h3>
                <p className="text-sm text-slate-600 mb-4">
                  Instale o aplicativo para acesso rápido, funcionamento offline e melhor experiência.
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button
                    onClick={handleInstall}
                    className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Instalar Agora
                  </Button>
                  <Button variant="outline" onClick={() => setShowModal(true)}>
                    Ver Instruções
                  </Button>
                </div>
              </div>
            </div>
            
            {/* Benefícios */}
            <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { icon: Zap, text: 'Mais rápido' },
                { icon: Monitor, text: 'Tela cheia' },
                { icon: Download, text: 'Funciona offline' },
                { icon: Check, text: 'Atalhos rápidos' },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2 text-sm text-slate-600">
                  <item.icon className="w-4 h-4 text-amber-600" />
                  <span>{item.text}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
        
        {showModal && <InstallModal onClose={() => setShowModal(false)} instructions={getInstructions()} />}
      </>
    );
  }

  // Variante: Banner (padrão)
  if (!showInstallBanner) return null;

  return (
    <>
      <div className="fixed bottom-4 left-4 right-4 z-50 md:left-auto md:right-4 md:max-w-md animate-slide-up">
        <Card className="bg-slate-900 border-amber-500/50 shadow-2xl shadow-amber-500/20">
          <CardContent className="p-4">
            <button 
              onClick={dismissBanner}
              className="absolute top-2 right-2 text-slate-400 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
            
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl flex items-center justify-center text-white flex-shrink-0">
                {getDeviceIcon()}
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-white font-semibold mb-1">Instalar nfcesysPdv</h4>
                <p className="text-slate-400 text-sm mb-3">
                  Acesso rápido direto do seu {deviceType === 'mobile' ? 'celular' : deviceType === 'tablet' ? 'tablet' : 'computador'}
                </p>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={handleInstall}
                    className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white"
                  >
                    <Download className="w-4 h-4 mr-1" />
                    Instalar
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={dismissBanner}
                    className="text-slate-400 hover:text-white"
                  >
                    Depois
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {showModal && <InstallModal onClose={() => setShowModal(false)} instructions={getInstructions()} />}
    </>
  );
};

// Modal de instruções
const InstallModal = ({ onClose, instructions }) => (
  <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
    <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-scale-up">
      <button 
        onClick={onClose}
        className="absolute top-4 right-4 text-slate-400 hover:text-slate-600"
      >
        <X className="w-5 h-5" />
      </button>
      
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
          <Download className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-xl font-bold text-slate-900">{instructions.title}</h2>
      </div>
      
      <div className="space-y-4 mb-6">
        {instructions.steps.map((step, index) => (
          <div key={index} className="flex items-start gap-3">
            <div className="w-7 h-7 bg-amber-100 text-amber-600 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">
              {index + 1}
            </div>
            <p className="text-slate-600 pt-0.5">{step}</p>
          </div>
        ))}
      </div>
      
      <div className="bg-slate-50 rounded-xl p-4 mb-4">
        <h4 className="font-semibold text-slate-900 mb-2">Benefícios do App:</h4>
        <ul className="space-y-1 text-sm text-slate-600">
          <li className="flex items-center gap-2">
            <Check className="w-4 h-4 text-emerald-500" />
            Acesso rápido direto da área de trabalho
          </li>
          <li className="flex items-center gap-2">
            <Check className="w-4 h-4 text-emerald-500" />
            Funciona mesmo sem internet
          </li>
          <li className="flex items-center gap-2">
            <Check className="w-4 h-4 text-emerald-500" />
            Interface em tela cheia
          </li>
          <li className="flex items-center gap-2">
            <Check className="w-4 h-4 text-emerald-500" />
            Notificações de pedidos
          </li>
        </ul>
      </div>
      
      <Button onClick={onClose} className="w-full">
        Entendi
      </Button>
    </div>
  </div>
);

export default InstallPWA;
